import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Card, Spinner, EmptyState, Badge } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Driver "Going Home" (Destination Mode) settings.
 *
 *  A driver can set a destination they're heading toward; while it's active the
 *  matcher only offers them trips that take them MEANINGFULLY closer to it (the
 *  on-the-way corridor lives in dispatch-svc). Uber caps how many times a driver
 *  can use it per day — this page edits that cap + turns the whole feature on/off.
 *
 *  admin-svc owns the singleton `driver_destination_settings` row (it reads +
 *  writes it directly, like fare_rates / docks); driver-svc READS it on every
 *  request so an edit here takes effect immediately. Gated by cms.manage to edit
 *  (the same perm the other settings pages use); degrades read-only if the table
 *  isn't in this Postgres yet. */
export function DestinationSettings() {
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'destination-settings'],
    queryFn: ({ signal }) => adminApi.destinationSettings(signal),
  });

  const available = data?.available !== false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver “Going Home”"
        subtitle="Destination Mode — the daily limit on how often a driver can filter trips toward a destination."
        right={
          <Badge tone={data?.enabled ? 'ok' : 'neutral'}>
            {!available ? 'not configured' : data?.enabled ? 'enabled' : 'disabled'}
          </Badge>
        }
      />

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading settings…" />
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState title="Couldn't load settings" description={(error as Error)?.message} />
        </Card>
      ) : !available ? (
        <Card>
          <EmptyState
            title="Not configured yet"
            description="The driver_destination_settings table isn’t in this database yet (driver-svc migration 056). It appears once that migration is applied."
          />
        </Card>
      ) : data ? (
        <SettingsForm dailyLimit={data.daily_limit} enabled={data.enabled} canManage={canManage} />
      ) : (
        <Card>
          <EmptyState title="No settings" />
        </Card>
      )}
    </AdminPage>
  );
}

function SettingsForm({
  dailyLimit,
  enabled,
  canManage,
}: {
  dailyLimit: number;
  enabled: boolean;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [limit, setLimit] = useState(String(dailyLimit));
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [clientError, setClientError] = useState<string | null>(null);

  // Re-seed when the server data changes (after a successful save / refetch).
  useEffect(() => {
    setLimit(String(dailyLimit));
    setIsEnabled(enabled);
  }, [dailyLimit, enabled]);

  const mutation = useMutation({
    mutationFn: (input: { daily_limit: number; enabled: boolean }) =>
      adminApi.updateDestinationSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'destination-settings'] }),
  });

  const onSave = () => {
    setClientError(null);
    const n = Number(limit.trim());
    if (!Number.isInteger(n) || n < 0 || n > 50) {
      setClientError('Daily limit must be a whole number between 0 and 50.');
      return;
    }
    mutation.mutate({ daily_limit: n, enabled: isEnabled });
  };

  const dirty = String(dailyLimit) !== limit.trim() || enabled !== isEnabled;

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Daily limit
        </div>
        <label className="block text-[13px] text-text-secondary">
          How many times a driver can switch on a destination per day (Uber uses 2).
        </label>
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            max="50"
            step="1"
            value={limit}
            disabled={!canManage}
            onChange={(e) => setLimit(e.target.value)}
            className="input w-28"
          />
          <span className="text-sm text-text-muted">trips / day</span>
        </div>

        <div className="mt-5 mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Feature
        </div>
        <label className="flex cursor-pointer items-center gap-2.5 text-[13px] text-text-secondary">
          <input
            type="checkbox"
            checked={isEnabled}
            disabled={!canManage}
            onChange={(e) => setIsEnabled(e.target.checked)}
            className="h-4 w-4 accent-brand-500"
          />
          Destination Mode is {isEnabled ? 'available to drivers' : 'turned off'}
        </label>

        {canManage ? (
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              disabled={mutation.isPending || !dirty}
              onClick={onSave}
              className="btn-primary"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {mutation.isSuccess && !dirty ? (
              <span className="text-xs text-success">Saved.</span>
            ) : null}
          </div>
        ) : (
          <p className="mt-4 text-xs text-text-muted">You don’t have permission to edit these settings.</p>
        )}

        {clientError ? <p className="mt-2 text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? (
          <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p>
        ) : null}
      </Card>
    </div>
  );
}
